import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CatalogIcon, CategoryIcon } from '@/shared/config';
import { categoriesApi } from '@/shared/api/categories';
import type { Category, Subcategory } from '@/entities/category';
import { useFavorites } from '@/features/favorites';
import { CatalogTile, CategoryGlyph } from '@/widgets/CatalogTile';
import { DetailBar } from '@/widgets/DetailBar';
import { EmptyState, PageMotion, Skeleton, staggerItem } from '@/shared/ui';
import { pluralRu } from '@/shared/lib/plural';
import { subcategoryPath } from '@/shared/lib/routes';
import styles from './Subcategories.module.css';

export const SubcategoriesPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    if (!categoryId) return;
    Promise.all([
      categoriesApi.list().then((r) => {
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : (raw as { data?: Category[] })?.data ?? [];
        return list.find((c) => c.id === categoryId) ?? null;
      }),
      categoriesApi.getSubcategories(categoryId).then((r) => {
        const raw = r.data;
        return Array.isArray(raw) ? raw : (raw as { data?: Subcategory[] })?.data ?? [];
      }),
    ])
      .then(([cat, subs]) => {
        setCategory(cat ?? null);
        setSubcategories(subs ?? []);
      })
      .catch(() => {
        setCategory(null);
        setSubcategories([]);
      })
      .finally(() => setLoading(false));
  }, [categoryId]);

  if (!categoryId) return null;

  const subtitle =
    category?.description ||
    (subcategories.length > 0
      ? `${subcategories.length} ${pluralRu(subcategories.length, 'подкатегория', 'подкатегории', 'подкатегорий')}`
      : null);

  return (
    <PageMotion>
      <main className={styles.wrapper}>
        { }
        <DetailBar backTo="/categories" backLabel="Каталог" noChatButton />

        {loading ? (
          <>
            <div className={styles.hero}>
              <Skeleton className={styles.heroGlyphSkeleton} radius="var(--radius-lg)" />
              <Skeleton className={styles.heroTitleSkeleton} />
            </div>
            <div className={styles.list}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className={styles.skeleton} radius="var(--radius-lg)" />
              ))}
            </div>
          </>
        ) : !category ? (
          <EmptyState
            Icon={CatalogIcon}
            title="Категория не найдена"
            hint="Возможно, её убрали из каталога или она недоступна вашей организации."
            action={<Link to="/categories">Вернуться в каталог</Link>}
          />
        ) : (
          <>
            <header className={styles.hero}>
              <CategoryGlyph icon={category.icon} seed={category.id} size="lg" />
              <div className={styles.heroText}>
                <h1 className={styles.pageTitle}>{category.name}</h1>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </div>
            </header>

            {subcategories.length === 0 ? (
              <EmptyState
                Icon={CategoryIcon}
                title="В категории пока нет подкатегорий"
                hint="Услуги в этой категории ещё не заведены — выберите другую."
                action={<Link to="/categories">Вернуться в каталог</Link>}
              />
            ) : (
              <motion.div
                className={styles.list}
                variants={{ animate: { transition: { staggerChildren: 0.015 } } }}
                initial="initial"
                animate="animate"
              >
                {subcategories.map((sub) => (
                  <motion.div key={sub.id} variants={staggerItem}>
                    <CatalogTile
                      to={subcategoryPath(categoryId, sub.id)}
                      titleAs="h2"
                      title={sub.name}
                      description={sub.description || undefined}
                      favorite={{
                        active: isFavorite('subcategory', sub.id),
                        onToggle: () => toggle('subcategory', sub.id),
                      }}
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}
      </main>
    </PageMotion>
  );
};
